import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/lib/auth";
import {
  fetchCoupons,
  fetchTeamOrders,
  createCoupon,
  deleteCoupon,
  couponShareUrl,
  getCouponTiers,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Plus,
  Share2,
  Copy,
  Ticket,
  Trash2,
  Sparkles,
  Loader2,
  X,
  TrendingUp,
  ShoppingBag,
  ChevronRight,
} from "lucide-react";

const HERO_DISMISS_KEY = "postcue.coupons.heroDismissed";

function formatCurrency(n) {
  const x = Number(n || 0);
  return x.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: x % 1 === 0 ? 0 : 2,
  });
}

function isExpired(date) {
  if (!date) return false;
  try {
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return false;
    if (d.getFullYear() < 2000) return false; // legacy "1970-01-01" sentinel
    return d.getTime() < Date.now();
  } catch {
    return false;
  }
}

export default function Coupons() {
  const { user } = useAuth();
  const userId = String(user?.user_id || user?.id || "");
  const userRecordId = user?.user_record_id || user?.record_id;

  const [loading, setLoading] = useState(true);
  const [coupons, setCoupons] = useState([]);
  const [teamOrders, setTeamOrders] = useState({
    count: 0,
    total_value: 0,
    orders: [],
    coupons_used: [],
  });
  const [error, setError] = useState(null);
  const [heroOpen, setHeroOpen] = useState(
    () => localStorage.getItem(HERO_DISMISS_KEY) !== "1"
  );
  const [createOpen, setCreateOpen] = useState(false);

  async function reload() {
    if (!userId) return;
    setLoading(true);
    try {
      const [c, t] = await Promise.all([
        fetchCoupons(userId),
        fetchTeamOrders(userId),
      ]);
      setCoupons(Array.isArray(c?.coupons) ? c.coupons : []);
      setTeamOrders(t || { count: 0, total_value: 0, orders: [], coupons_used: [] });
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const activeCoupons = useMemo(
    () => coupons.filter((c) => !isExpired(c.expiry_date)).length,
    [coupons]
  );

  function dismissHero() {
    localStorage.setItem(HERO_DISMISS_KEY, "1");
    setHeroOpen(false);
  }

  return (
    <div className="px-4 pt-4 pb-32" data-testid="coupons-page">
      {heroOpen && <Hero onDismiss={dismissHero} />}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <StatCard
          icon={Ticket}
          label="Active coupons"
          value={activeCoupons}
          testId="stat-active-coupons"
        />
        <StatCard
          icon={ShoppingBag}
          label="Team orders"
          value={teamOrders.count || 0}
          testId="stat-team-orders"
        />
        <StatCard
          icon={TrendingUp}
          label="Team revenue"
          value={formatCurrency(teamOrders.total_value)}
          testId="stat-team-revenue"
        />
      </div>

      {/* Title + create */}
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg font-semibold tracking-tight text-zinc-900">
          Your coupons
        </h2>
        <Button
          size="sm"
          className="rounded-full bg-violet-600 hover:bg-violet-700 text-white font-medium px-3"
          onClick={() => setCreateOpen(true)}
          data-testid="coupons-create-btn"
        >
          <Plus size={16} className="mr-1" /> New
        </Button>
      </div>

      {loading ? (
        <SkeletonList />
      ) : error ? (
        <Card className="p-4 border-red-200 bg-red-50 text-sm text-red-700" data-testid="coupons-error">
          {error}
        </Card>
      ) : coupons.length === 0 ? (
        <EmptyCoupons onCreate={() => setCreateOpen(true)} />
      ) : (
        <ul className="space-y-2" data-testid="coupons-list">
          {coupons.map((c) => (
            <CouponCard key={c.id} coupon={c} userRecordId={userRecordId} onDeleted={reload} />
          ))}
        </ul>
      )}

      {/* Recent team orders */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-semibold tracking-tight text-zinc-900">
            Recent orders from your coupons
          </h2>
        </div>
        {loading ? (
          <SkeletonList rows={2} />
        ) : teamOrders.orders.length === 0 ? (
          <Card className="rounded-2xl border-dashed border-zinc-300 p-6 text-center text-sm text-zinc-500" data-testid="team-orders-empty">
            No team orders yet. Share your coupon to start earning.
          </Card>
        ) : (
          <ul className="space-y-2" data-testid="team-orders-list">
            {teamOrders.orders.slice(0, 6).map((o) => (
              <OrderRow key={o.id} order={o} />
            ))}
          </ul>
        )}
      </div>

      <CreateCouponDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        userRecordId={userRecordId}
        existingNames={coupons.map((c) => c.coupon_name).filter(Boolean)}
        onCreated={() => {
          setCreateOpen(false);
          reload();
        }}
      />
    </div>
  );
}

// ---------- Sub-components ----------
function Hero({ onDismiss }) {
  return (
    <div
      className="relative rounded-2xl bg-gradient-to-br from-violet-600 via-violet-700 to-fuchsia-700 text-white p-5 mb-4 overflow-hidden"
      data-testid="coupons-hero"
    >
      <button
        onClick={onDismiss}
        className="absolute top-3 right-3 w-7 h-7 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center"
        aria-label="Dismiss"
        data-testid="coupons-hero-dismiss"
      >
        <X size={14} />
      </button>
      <div className="inline-flex items-center gap-1.5 text-[11px] font-medium bg-white/15 px-2 py-1 rounded-full mb-3">
        <Sparkles size={12} /> Share &amp; Earn Rewards
      </div>
      <h1 className="text-2xl font-semibold leading-tight tracking-tight">
        Turn your glow into income.
      </h1>
      <p className="text-sm text-white/85 mt-1.5 leading-relaxed">
        Create a coupon, share it anywhere — text, social, QR at your spa.
        Earn up to <span className="font-semibold">60% commission</span> on every order.
      </p>
      <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
        <span className="bg-white/15 rounded-full px-2 py-1">1 · Create coupon</span>
        <span className="bg-white/15 rounded-full px-2 py-1">2 · Share anywhere</span>
        <span className="bg-white/15 rounded-full px-2 py-1">3 · Earn automatically</span>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testId }) {
  return (
    <Card
      className="rounded-2xl border-zinc-200 p-3 bg-white"
      data-testid={testId}
    >
      <Icon size={16} className="text-violet-600" />
      <div className="mt-1 text-lg font-semibold tracking-tight text-zinc-900 leading-none">
        {value}
      </div>
      <div className="text-[11px] text-zinc-500 mt-1">{label}</div>
    </Card>
  );
}

function CouponCard({ coupon, userRecordId, onDeleted }) {
  const code = coupon.coupon_name;
  const expired = isExpired(coupon.expiry_date);
  const shareUrl = couponShareUrl(code);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  async function remove() {
    setDeleting(true);
    try {
      await deleteCoupon(coupon.id, userRecordId);
      toast.success(`Coupon ${code} deleted`);
      onDeleted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete coupon");
      setDeleting(false);
      setConfirmDelete(false);
    }
  }

  async function copyCode() {
    await navigator.clipboard.writeText(code);
    toast.success(`Coupon ${code} copied`);
  }
  async function copyLink() {
    await navigator.clipboard.writeText(shareUrl);
    toast.success("Share link copied");
  }
  async function share() {
    const data = {
      title: `OreVital discount: ${code}`,
      text: `Use my code ${code} on OreVital for a discount.`,
      url: shareUrl,
    };
    if (navigator.share) {
      try {
        await navigator.share(data);
      } catch {
        /* user cancelled */
      }
    } else {
      copyLink();
    }
  }

  return (
    <li
      className="rounded-2xl border border-zinc-200 bg-white p-3"
      data-testid={`coupon-card-${coupon.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl bg-violet-50 flex items-center justify-center shrink-0">
          <Ticket className="text-violet-700" size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={copyCode}
              className="font-semibold text-zinc-900 tracking-tight hover:text-violet-700"
              data-testid={`coupon-code-${coupon.id}`}
            >
              {code}
            </button>
            <span
              className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${
                expired
                  ? "bg-zinc-100 text-zinc-500"
                  : "bg-emerald-50 text-emerald-700"
              }`}
            >
              {expired ? "Expired" : "Active"}
            </span>
          </div>
          <div className="text-xs text-zinc-500 mt-0.5 flex flex-wrap items-center gap-x-2">
            <span className="capitalize">{coupon.coupon_tier || "—"}</span>
            {coupon.amount ? (
              <>
                <span className="text-zinc-300">·</span>
                <span>
                  {coupon.coupon_type === "percent"
                    ? `${coupon.amount}% off`
                    : formatCurrency(coupon.amount) + " off"}
                </span>
              </>
            ) : null}
            {coupon.expiry_date && coupon.expiry_date.startsWith("2") && (
              <>
                <span className="text-zinc-300">·</span>
                <span>exp {coupon.expiry_date}</span>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <Button
          size="sm"
          onClick={share}
          className="flex-1 h-9 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-medium"
          data-testid={`coupon-share-${coupon.id}`}
        >
          <Share2 size={15} className="mr-1.5" /> Share
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={copyLink}
          className="h-9 rounded-lg border-zinc-200"
          data-testid={`coupon-copy-link-${coupon.id}`}
        >
          <Copy size={15} className="mr-1.5" /> Link
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setConfirmDelete(true)}
          className="h-9 w-9 p-0 rounded-lg border-zinc-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
          aria-label="Delete coupon"
          data-testid={`coupon-delete-${coupon.id}`}
        >
          <Trash2 size={15} />
        </Button>
      </div>
      {confirmDelete && (
        <div className="mt-2 flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2" data-testid={`coupon-delete-confirm-${coupon.id}`}>
          <span className="flex-1 text-xs text-rose-800">Delete <b>{code}</b>? Customers can no longer use it.</span>
          <Button size="sm" onClick={remove} disabled={deleting} className="h-8 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs" data-testid={`coupon-delete-yes-${coupon.id}`}>
            {deleting ? <Loader2 size={12} className="animate-spin" /> : "Delete"}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(false)} disabled={deleting} className="h-8 rounded-lg text-xs" data-testid={`coupon-delete-no-${coupon.id}`}>
            Keep
          </Button>
        </div>
      )}
    </li>
  );
}

function OrderRow({ order }) {
  const buyer = [order.customer_first_name, order.customer_last_name]
    .filter(Boolean)
    .join(" ") || order.name || "Customer";
  return (
    <li
      className="rounded-xl bg-white border border-zinc-200 p-3 flex items-center gap-3"
      data-testid={`team-order-${order.id}`}
    >
      <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
        <ShoppingBag className="text-emerald-700" size={18} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className="font-medium text-zinc-900 truncate">{buyer}</div>
          <span className="text-[10px] uppercase tracking-wider font-semibold bg-violet-50 text-violet-700 px-1.5 py-0.5 rounded">
            {order.coupon_code}
          </span>
        </div>
        <div className="text-xs text-zinc-500 truncate">
          #{order.order_id} · {order.date}
          {order.status ? ` · ${order.status}` : ""}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="font-semibold text-zinc-900">
          {formatCurrency(order.total)}
        </div>
        <div className="text-[10px] text-zinc-400">
          {order.item_count ? `${order.item_count} item${order.item_count > 1 ? "s" : ""}` : ""}
        </div>
      </div>
      <ChevronRight className="text-zinc-300" size={16} />
    </li>
  );
}

function EmptyCoupons({ onCreate }) {
  return (
    <Card
      className="rounded-2xl border-dashed border-zinc-300 p-8 text-center"
      data-testid="coupons-empty"
    >
      <div className="w-12 h-12 rounded-2xl bg-violet-100 mx-auto flex items-center justify-center mb-3">
        <Ticket className="text-violet-600" size={22} />
      </div>
      <div className="font-semibold text-zinc-900 mb-1">No coupons yet</div>
      <p className="text-sm text-zinc-500 max-w-xs mx-auto mb-4">
        Create your first coupon to start sharing and earning commission.
      </p>
      <Button
        onClick={onCreate}
        className="bg-violet-600 hover:bg-violet-700 text-white rounded-xl"
        data-testid="coupons-empty-create-btn"
      >
        <Plus size={16} className="mr-1" /> Create coupon
      </Button>
    </Card>
  );
}

function SkeletonList({ rows = 3 }) {
  return (
    <ul className="space-y-2" data-testid="coupons-skeleton">
      {Array.from({ length: rows }).map((_, i) => (
        <li
          key={i}
          className="rounded-2xl border border-zinc-200 bg-white p-3 animate-pulse h-24"
        />
      ))}
    </ul>
  );
}

function CreateCouponDialog({
  open,
  onOpenChange,
  userRecordId,
  existingNames,
  onCreated,
}) {
  const [name, setName] = useState("");
  const [tiers, setTiers] = useState([]);
  const [tier, setTier] = useState("");
  const [amount, setAmount] = useState("");
  const [expiry, setExpiry] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    getCouponTiers(userRecordId)
      .then((res) => {
        const list = res?.tiers || [];
        setTiers(list);
        setTier((cur) => cur || list[0]?.value || "");
      })
      .catch(() => {});
  }, [open, userRecordId]);

  function reset() {
    setName("");
    setTier(tiers[0]?.value || "");
    setAmount("");
    setExpiry("");
  }

  async function submit() {
    const clean = name.trim().toUpperCase().replace(/\s+/g, "");
    if (!clean) return toast.error("Coupon code is required");
    if (clean.length < 3) return toast.error("Coupon code is too short");
    if (!tier) return toast.error("Pick a tier");
    if (existingNames.map((n) => n.toUpperCase()).includes(clean))
      return toast.error("You already have a coupon with that code");
    setSaving(true);
    try {
      await createCoupon({
        user_record_id: userRecordId,
        coupon_name: clean,
        coupon_tier: tier,
        coupon_type: "role-discount",
        amount: amount ? Number(amount) : null,
        expiry_date: expiry || null,
      });
      toast.success(`Coupon ${clean} created`);
      reset();
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Failed to create");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl p-5 max-w-md" data-testid="create-coupon-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl">Create a coupon</DialogTitle>
          <DialogDescription>
            Pick a memorable code — your customers will type it at checkout.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div>
            <Label htmlFor="cname">Coupon code</Label>
            <Input
              id="cname"
              value={name}
              onChange={(e) => setName(e.target.value.toUpperCase().replace(/\s+/g, ""))}
              placeholder="e.g. GLOW20"
              className="h-11 rounded-xl mt-1.5 uppercase"
              data-testid="create-coupon-name-input"
            />
          </div>

          <div>
            <Label>Tier</Label>
            <Select value={tier} onValueChange={setTier}>
              <SelectTrigger
                className="h-11 rounded-xl mt-1.5"
                data-testid="create-coupon-tier-select"
              >
                <SelectValue placeholder="Select a tier" />
              </SelectTrigger>
              <SelectContent>
                {tiers.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="amt">Amount</Label>
              <Input
                id="amt"
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))}
                placeholder="Optional"
                className="h-11 rounded-xl mt-1.5"
                data-testid="create-coupon-amount-input"
              />
            </div>
            <div>
              <Label htmlFor="exp">Expiry date</Label>
              <Input
                id="exp"
                type="date"
                value={expiry}
                onChange={(e) => setExpiry(e.target.value)}
                className="h-11 rounded-xl mt-1.5"
                data-testid="create-coupon-expiry-input"
              />
            </div>
          </div>
        </div>

        <DialogFooter className="mt-5">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            data-testid="create-coupon-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="create-coupon-submit-btn"
          >
            {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
            Create coupon
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
